import { useCallback, useRef, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { useQueryClient } from '@tanstack/react-query'
import { BLOGGER_STAGE_ORDER, BloggerStage } from '@ayub/shared'
import type { BloggerListItem } from '@ayub/shared'
import type { BloggerDragData } from './BloggerKanbanCard'
import { BloggerKanbanCardContent } from './BloggerKanbanCard'
import { BloggerKanbanColumn } from './BloggerKanbanColumn'
import { useBloggerColumn } from './hooks'

interface PendingMove {
  blogger: BloggerListItem
  from: BloggerStage
  to: BloggerStage
}

interface BloggerKanbanViewProps {
  searchTerm: string
  onSelectBlogger: (id: number) => void
  /**
   * Sudrash — bosqichni O'ZGARTIRISH SO'ROVI, o'zgarishning o'zi emas: `Rad etildi`ga
   * ko'chirish sabab talab qiladi, buni faqat kartochka (modal) yig'a oladi (parallel
   * agent quradi). Shu sababli kartochka bu yerda faqat OPTIMISTIK ko'chadi va integratsiya
   * `true`/`false` bilan javob berguncha kutadi: `true` — qabul qilindi (server so'raladi va
   * optimistik holat shu bilan almashtiriladi), `false` — rad etildi (kartochka darhol
   * eski ustuniga qaytadi).
   */
  onStageChange: (
    bloggerId: number,
    newStage: BloggerStage,
    previousStage: BloggerStage,
  ) => Promise<boolean> | boolean
}

/**
 * Blogerlar kanbani — `docs/03-app-flow.md` Sahifa 3: "Yangi ariza → Faol → Rad etildi →
 * Bloklangan". Har bir ustun o'z sahifalangan so'rovini yuritadi (`useBloggerColumn`); bu
 * komponent faqat sudrash holatini va optimistik ko'chirishni boshqaradi.
 */
export function BloggerKanbanView({
  searchTerm,
  onSelectBlogger,
  onStageChange,
}: BloggerKanbanViewProps) {
  const queryClient = useQueryClient()

  // 4 ta bosqich — soni doim bir xil va o'zgarmas (`BLOGGER_STAGE_ORDER`), shuning uchun
  // hook'lar sikl ichida emas, aniq chaqirilgan (React hook tartibi hech qachon o'zgarmaydi).
  const newColumn = useBloggerColumn(BloggerStage.NEW, searchTerm)
  const activeColumn = useBloggerColumn(BloggerStage.ACTIVE, searchTerm)
  const rejectedColumn = useBloggerColumn(BloggerStage.REJECTED, searchTerm)
  const blockedColumn = useBloggerColumn(BloggerStage.BLOCKED, searchTerm)

  const serverColumns = {
    [BloggerStage.NEW]: newColumn,
    [BloggerStage.ACTIVE]: activeColumn,
    [BloggerStage.REJECTED]: rejectedColumn,
    [BloggerStage.BLOCKED]: blockedColumn,
  } as const

  const [pendingMoves, setPendingMoves] = useState<Map<number, PendingMove>>(new Map())
  const [activeDrag, setActiveDrag] = useState<BloggerDragData | null>(null)
  // Chinakam sudrashdan keyin keladigan tasodifiy "click" hodisasini bosib qo'yish uchun —
  // aks holda kartochka sudrab tashlangandan so'ng kartochka kartasi (modal) ham ochilib
  // ketadi. Faqat sensor faollashganda (haqiqiy sudrash) `true` bo'ladi — oddiy bosishda
  // (harakatsiz) `onDragStart` umuman chaqirilmaydi (pastdagi `activationConstraint`larga
  // qarang), shuning uchun oddiy bosish hech qachon bosib qo'yilmaydi.
  const dragOccurredRef = useRef(false)

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    // 200ms bosib turish — `docs/04-ui-ux.md`: "Kanban sudrash mobil sensorli ekranda
    // ishlaydi (dnd-kit TouchSensor, 200ms bosib turish)". Bu muddat ichida barmoq 8px dan
    // ko'p siljisa, bu SKROLL deb hisoblanadi, sudrash boshlanmaydi — aks holda ustunni
    // pastga-tepaga suriш har safar tasodifan kartochkani sudrab yuboradi (eng ko'p
    // uchraydigan mobil kanban nosozligi).
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    dragOccurredRef.current = true
    const data = event.active.data.current as BloggerDragData | undefined
    setActiveDrag(data ?? null)
  }, [])

  const clearDragFlagSoon = () => {
    setTimeout(() => {
      dragOccurredRef.current = false
    }, 0)
  }

  const handleDragCancel = useCallback(() => {
    setActiveDrag(null)
    clearDragFlagSoon()
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDrag(null)
      clearDragFlagSoon()

      const data = event.active.data.current as BloggerDragData | undefined
      const overId = event.over?.id
      if (!data || overId === undefined) return

      const destStage = overId as BloggerStage
      const sourceStage = data.stage
      if (destStage === sourceStage) return

      const blogger = data.blogger
      // Xuddi shu obyekt havolasi pastda "faqat MENING pending yozuvim hali ham amaldami"
      // tekshiruvi uchun ishlatiladi — agar shu bloger tez orada (birinchi so'rov hali
      // tugamasdan) YANA sudralsa, ikkinchi chaqiruv o'zining yangi yozuvini qo'yadi va
      // birinchisi tugaganda ESKI (allaqachon almashtirilgan) yozuvni o'chirib
      // yubormasligi kerak.
      const moveRecord: PendingMove = { blogger, from: sourceStage, to: destStage }
      setPendingMoves((prev) => new Map(prev).set(blogger.id, moveRecord))

      void Promise.resolve(onStageChange(blogger.id, destStage, sourceStage)).then(
        async (accepted) => {
          if (accepted) {
            // Optimistik holatni yashirin saqlab turamiz (pastda tozalanguncha), toki
            // haqiqiy server ma'lumoti kelmaguncha kartochka ustundan ustunga "sakramasin".
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ['bloggers', 'kanban', sourceStage] }),
              queryClient.invalidateQueries({ queryKey: ['bloggers', 'kanban', destStage] }),
            ])
            void queryClient.invalidateQueries({ queryKey: ['bloggers', 'stageCounts'] })
            void queryClient.invalidateQueries({ queryKey: ['bloggers', 'list'] })
          }
          setPendingMoves((prev) => {
            if (prev.get(blogger.id) !== moveRecord) return prev
            const next = new Map(prev)
            next.delete(blogger.id)
            return next
          })
        },
      )
    },
    [onStageChange, queryClient],
  )

  const handleCardClick = useCallback(
    (id: number) => {
      if (dragOccurredRef.current) return
      onSelectBlogger(id)
    },
    [onSelectBlogger],
  )

  const displayItems = (stage: BloggerStage): BloggerListItem[] => {
    const serverItems = serverColumns[stage].items.filter((item) => !pendingMoves.has(item.id))
    const movedIn: BloggerListItem[] = []
    for (const move of pendingMoves.values()) {
      if (move.to === stage) movedIn.push(move.blogger)
    }
    return [...movedIn, ...serverItems]
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 sm:snap-none">
        {BLOGGER_STAGE_ORDER.map((stage) => {
          const column = serverColumns[stage]
          return (
            <BloggerKanbanColumn
              key={stage}
              stage={stage}
              items={displayItems(stage)}
              total={column.total}
              isLoading={column.isLoading}
              isFetching={column.isFetching}
              error={column.error}
              hasMore={column.hasMore}
              onLoadMore={column.loadMore}
              onCardClick={handleCardClick}
            />
          )
        })}
      </div>

      <DragOverlay>
        {activeDrag && (
          <BloggerKanbanCardContent
            blogger={activeDrag.blogger}
            displayStage={activeDrag.stage}
            dragging
          />
        )}
      </DragOverlay>
    </DndContext>
  )
}
